/**
 * Vista de Eventos para Admin de Asociación
 * - Crear y gestionar eventos de la asociación
 * - Ver inscripciones de todos los clubes
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Button,
  Badge,
  SectionBadge,
} from "@/components/ui";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import {
  Calendar,
  Plus,
  Search,
  MapPin,
  Users,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  Play,
  Send,
  ClipboardList,
  BarChart3,
  Eye,
} from "lucide-react";
import {
  useEvents,
  useUpdateEventStatus,
  useDeleteEvent,
} from "@/hooks/useEvents";
import { useAuthStore } from "@/stores/authStore";
import { useAssociation } from "@/hooks/useOrganization";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import EventModal from "./EventModal";
import EventRegistrationsPanel from "./EventRegistrationsPanel";
import PaymentValidationPanel from "./PaymentValidationPanel";
import type { Event, ClubType } from "@/types";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

const eventTypeLabels: Record<string, string> = {
  camporee: "🏕️ Camporee",
  campamento: "⛺ Campamento",
  convocatoria: "📢 Convocatoria",
  entrenamiento: "📚 Entrenamiento",
  investidura: "🎖️ Investidura",
  social: "🎉 Social",
  servicio: "🤝 Servicio",
  otro: "📌 Otro",
};

type TabType = "upcoming" | "all" | "drafts";

export default function AssociationEventsView() {
  const [activeTab, setActiveTab] = useState<TabType>("upcoming");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showRegistrationsPanel, setShowRegistrationsPanel] = useState(false);
  const [registrationsPanelEvent, setRegistrationsPanelEvent] =
    useState<Event | null>(null);
  const [showPaymentsPanel, setShowPaymentsPanel] = useState(false);
  const [paymentsPanelEvent, setPaymentsPanelEvent] = useState<Event | null>(
    null,
  );
  const navigate = useNavigate();

  const handleViewDetail = (event: Event) => {
    navigate(`/events/${event.id}`);
  };

  const { data: events, isLoading } = useEvents();
  const { data: clubs } = useAccessibleClubs();
  const { profile } = useAuthStore();
  const { isUnionAdmin } = usePermissions();
  const { data: association } = useAssociation(profile?.scopeId || "");
  const updateStatusMutation = useUpdateEventStatus();
  const deleteMutation = useDeleteEvent();
  const confirm = useConfirm();

  // Eventos de la asociación (union admin ve todos)
  const associationEvents = useMemo(() => {
    if (!events) return [];
    if (isUnionAdmin) return events;
    if (!profile?.scopeId) return [];
    return events.filter((e) => e.organizerId === profile.scopeId);
  }, [events, profile?.scopeId, isUnionAdmin]);

  // Eventos de clubes de la asociación
  const clubEvents = useMemo(() => {
    if (!events || !clubs) return [];
    const clubIds = new Set(clubs.map((c) => c.id));
    return events.filter((e) => clubIds.has(e.organizerId));
  }, [events, clubs]);

  // Filtrar según tab activo
  const filteredEvents = useMemo(() => {
    const now = new Date();
    let result = [...associationEvents];

    if (activeTab === "upcoming") {
      result = result.filter(
        (e) =>
          ["published", "registration_closed", "in_progress"].includes(
            e.status,
          ) && toSafeDate(e.startDate) >= now,
      );
    } else if (activeTab === "drafts") {
      result = result.filter((e) => e.status === "draft");
    }

    if (searchTerm) {
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.location.city.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return result;
  }, [associationEvents, activeTab, searchTerm]);

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleViewRegistrations = (event: Event) => {
    setRegistrationsPanelEvent(event);
    setShowRegistrationsPanel(true);
  };

  const handleViewPayments = (event: Event) => {
    setPaymentsPanelEvent(event);
    setShowPaymentsPanel(true);
  };

  const handlePublish = async (event: Event) => {
    await updateStatusMutation.mutateAsync({
      id: event.id,
      status: "published",
    });
  };

  const handleCloseRegistration = async (event: Event) => {
    await updateStatusMutation.mutateAsync({
      id: event.id,
      status: "registration_closed",
    });
  };

  const handleStartEvent = async (event: Event) => {
    await updateStatusMutation.mutateAsync({
      id: event.id,
      status: "in_progress",
    });
  };

  const handleCompleteEvent = async (event: Event) => {
    await updateStatusMutation.mutateAsync({
      id: event.id,
      status: "completed",
    });
  };

  const handleDeleteEvent = async (event: Event) => {
    const ok = await confirm({
      title: "Eliminar evento",
      description: `¿Seguro que deseas eliminar el evento "${event.name}"? Se perderán las inscripciones asociadas.`,
      variant: "danger",
      confirmText: "Eliminar",
    });
    if (ok) {
      await deleteMutation.mutateAsync(event.id);
    }
  };

  const formatDate = (timestamp: unknown) => {
    return toSafeDate(timestamp).toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateRange = (start: unknown, end: unknown) => {
    const startDate = toSafeDate(start);
    const endDate = toSafeDate(end);

    if (startDate.toDateString() === endDate.toDateString()) {
      return formatDate(start);
    }

    return `${startDate.toLocaleDateString("es-CO", { day: "numeric", month: "short" })} - ${endDate.toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            Gestión de Eventos
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Crea y administra eventos para{" "}
            {association?.name || "la asociación"}
          </p>
        </div>
        <Can permission="events.write">
          <Button
            onClick={() => setShowEventModal(true)}
            disabled={!association}
            className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Evento
          </Button>
        </Can>
      </div>

      {/* Club Events Summary */}
      {clubEvents.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-primary-50 border-blue-200 border-0 shadow-md">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">
                    Eventos de Clubes: {clubEvents.length}
                  </p>
                  <p className="text-sm text-blue-700">
                    {clubEvents.reduce(
                      (sum, e) => sum + (e.registeredCount || 0),
                      0,
                    )}{" "}
                    inscripciones totales
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("upcoming")}
          className={`px-4 py-2 font-medium transition-colors duration-200 ${
            activeTab === "upcoming"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Próximos
        </button>
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 font-medium transition-colors duration-200 ${
            activeTab === "all"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Todos ({associationEvents.length})
        </button>
        <button
          onClick={() => setActiveTab("drafts")}
          className={`px-4 py-2 font-medium transition-colors duration-200 ${
            activeTab === "drafts"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Borradores (
          {associationEvents.filter((e) => e.status === "draft").length})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar eventos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
          className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
        />
      </div>

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Calendar className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              No hay eventos
            </h3>
            <p className="text-gray-500 mt-1">
              {activeTab === "drafts"
                ? "No tienes eventos en borrador"
                : "Crea un nuevo evento para la asociación"}
            </p>
            <Can permission="events.write">
              <Button onClick={() => setShowEventModal(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Crear Evento
              </Button>
            </Can>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => (
            <Card
              key={event.id}
              className="hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="flex">
                {/* Imagen del evento */}
                {event.coverImageUrl ? (
                  <div className="w-48 h-auto flex-shrink-0">
                    <img
                      src={event.coverImageUrl}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-48 h-auto flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Calendar className="w-12 h-12 text-white/50" />
                  </div>
                )}

                <CardContent className="p-6 flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">
                          {eventTypeLabels[event.eventType] || event.eventType}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {event.name}
                        </h3>
                        <Badge className={getEventStatusColor(event.status)}>
                          {getEventStatusLabel(event.status)}
                        </Badge>
                      </div>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {event.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDateRange(event.startDate, event.endDate)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {event.registeredCount || 0} inscritos
                          {event.registrationConfig?.maxCapacity &&
                            ` / ${event.registrationConfig.maxCapacity}`}
                        </span>
                      </div>

                      {/* Club Types */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {event.clubTypesAllowed?.map((type) => (
                          <SectionBadge
                            key={type}
                            section={type as ClubType}
                            size="xs"
                            variant="soft"
                          />
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {/* Status Actions */}
                      {event.status === "draft" && (
                        <Button
                          size="sm"
                          onClick={() => handlePublish(event)}
                          disabled={updateStatusMutation.isPending}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Publicar
                        </Button>
                      )}
                      {event.status === "published" && (
                        <button
                          onClick={() => handleCloseRegistration(event)}
                          className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-colors duration-200"
                          title="Cerrar inscripciones"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                      {event.status === "registration_closed" && (
                        <button
                          onClick={() => handleStartEvent(event)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-xl transition-colors duration-200"
                          title="Iniciar evento"
                        >
                          <Play className="w-5 h-5" />
                        </button>
                      )}
                      {event.status === "in_progress" && (
                        <button
                          onClick={() => handleCompleteEvent(event)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors duration-200"
                          title="Marcar como completado"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      )}

                      {/* Common Actions */}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleViewDetail(event)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Detalle
                      </Button>
                      <button
                        onClick={() => handleViewRegistrations(event)}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors duration-200"
                        title="Ver inscripciones"
                      >
                        <ClipboardList className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleViewPayments(event)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-xl transition-colors duration-200"
                        title="Validar pagos"
                      >
                        <BarChart3 className="w-5 h-5" />
                      </button>
                      <Can permission="events.write">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      </Can>
                      {event.status === "draft" && (
                        <Can permission="events.approve">
                          <button
                            onClick={() => handleDeleteEvent(event)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200"
                            title="Eliminar"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </Can>
                      )}
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      {association && (
        <EventModal
          isOpen={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          organizerId={association.id}
          organizerName={association.name}
          event={selectedEvent}
        />
      )}

      {registrationsPanelEvent && (
        <EventRegistrationsPanel
          isOpen={showRegistrationsPanel}
          onClose={() => {
            setShowRegistrationsPanel(false);
            setRegistrationsPanelEvent(null);
          }}
          event={registrationsPanelEvent}
          canManage={true}
        />
      )}

      {paymentsPanelEvent && (
        <PaymentValidationPanel
          isOpen={showPaymentsPanel}
          onClose={() => {
            setShowPaymentsPanel(false);
            setPaymentsPanelEvent(null);
          }}
          event={paymentsPanelEvent}
        />
      )}
    </div>
  );
}
